import { FC, Fragment, Dispatch, SetStateAction } from "react";
import { Box, useTheme } from "@mui/material";
import { FormikHelpers } from "formik";
import {
  DateRangePicker,
  Button,
  toast,
  TableToggleField
} from "@vilocnv/allsetra-core";
import { GroupBy } from "app/data/helpers";
import ReportDateFormats from "../ReportDateFormats/ReportDateFormats";
import ExportButtonMenu from "../../ExportButtonMenu/ExportButtonMenu";
import ScheduleReports from "../ScheduleReports/ScheduleReports";
import { useAppDispatch, useAppSelector, useReportCommonStates } from "hooks";
import { selectAccountFeaturesState } from "app/data/selectors";
import {
  ExportReportIcon,
  ExportReportActiveIcon,
  RefreshIcon
} from "assets/icons";
import { MuiButton } from "pages/dashboard/Reports/Reports.styled";
import ExportReportsForm from "components/forms/Reports/ExportReportsForm";
import { omit } from "lodash";
import { exportReportThunk } from "app/features";
import { useTranslation } from "react-i18next";
import { useMovementRideContext } from "contexts/MovementRideContext";

interface Props {
  setFilterOpen: (value: boolean) => void;
  dateFormat: string;
  setDateFormat: Dispatch<SetStateAction<GroupBy>>;
  handleDateChange: (value: any) => void;
  dateValues: any;
  handleDateRangeSubmit: () => void;
  dateRangeLoading: boolean;
  exportHeaders?: Array<any>;
  generateCSVData?: () => any[];
  formattedData?: any;
  generatePDF?: () => void;
  hideExportButton?: boolean;
  hideScheduleButton?: boolean;
  hideLoadReportButton?: boolean;
  hideDateRangePicker?: boolean;
  disableExportButton?: boolean;
  exportButtonLoading?: boolean;
  reportType?: number;
  reportExportTypeValue?: number;
  openModal?: boolean;
  setOpenModal?: (value: boolean) => void;
  exportModalButton?: boolean;
  hideDateFormats?: boolean;
  showRideModes?: boolean;
  handleLoadReport?: () => void;
  handleGenerateReport?: () => void;
  showToggleMovementRides?: boolean;
}

const ReportsFilterBar: FC<Props> = ({
  setFilterOpen,
  dateFormat,
  setDateFormat,
  handleDateChange,
  dateValues,
  handleDateRangeSubmit,
  dateRangeLoading,
  exportHeaders = [],
  generateCSVData,
  formattedData,
  generatePDF,
  hideExportButton = false,
  disableExportButton = false,
  exportButtonLoading = false,
  hideScheduleButton = false,
  hideLoadReportButton = false,
  hideDateRangePicker = false,
  reportType,
  reportExportTypeValue,
  openModal = false,
  setOpenModal,
  exportModalButton = false,
  hideDateFormats = false,
  handleLoadReport = () => null,
  handleGenerateReport = () => null,
  showToggleMovementRides = false
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { showMovementRides, handleMovementRidesChange } =
    useMovementRideContext();

  const { t } = useTranslation();

  const { features } = useAppSelector(selectAccountFeaturesState);

  const { reportsFilterData } = useReportCommonStates();

  const exportReportsSubmitHandler = async (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    formikHelpers.setSubmitting(true);
    const { emailAddress, exportType } = values;

    const updatedPayload = omit(reportsFilterData, [
      "driver",
      "showInactiveObjects"
    ]);

    const payload = {
      reportDefinition: {
        ...updatedPayload,
        startedWithoutIgnition: showMovementRides
      },
      emailAddress,
      exportType
    };

    const { type } = await dispatch(exportReportThunk(payload));

    if (type === "reports/exportReportThunk/fulfilled") {
      formikHelpers.setSubmitting(false);
      formikHelpers.resetForm();
      toast.success(t("reports.exportSuccessMessage"));
      if (setOpenModal) setOpenModal(false);
    } else {
      toast.error("Server Error");
    }
  };

  return (
    <Box my={2} display={"flex"} justifyContent={"space-between"}>
      <Box display={"flex"}>
        <Button
          id="filter-button"
          sx={{ mr: 2 }}
          text="Filter"
          size="small"
          theme={theme}
          onClick={() => setFilterOpen(true)}
        />
        {!hideDateFormats && (
          <ReportDateFormats
            dateFormat={dateFormat}
            setDateFormat={setDateFormat}
          />
        )}
        {!hideDateRangePicker && (
          <DateRangePicker
            name="datepicker"
            onChange={handleDateChange}
            value={dateValues}
            onSubmit={handleDateRangeSubmit}
            disabled={dateRangeLoading}
            theme={theme}
            translator={t}
            disableFutureDates
          />
        )}
        {!hideLoadReportButton && (
          <Button
            id="load-report-button"
            sx={{ mx: 2 }}
            size="small"
            theme={theme}
            onClick={handleLoadReport}
            disabled={dateRangeLoading}
          >
            <RefreshIcon style={{ marginRight: "10px" }} />
            {t("buttonsText.loadReport")}
          </Button>
        )}
        {showToggleMovementRides && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              "& .MuiSwitch-switchBase": {
                padding: "3px"
              }
            }}
          >
            <TableToggleField
              label={t("common.showMovementRides") ?? "Show movement rides"}
              name="showMovementRides"
              onChange={(e) => handleMovementRidesChange(e.target.checked)}
              checked={showMovementRides}
            />
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: "30px" }}>
        {!hideScheduleButton && features.isAutomaticReportsEnabled && (
          <ScheduleReports
            reportType={reportType}
            reportExportTypeValue={reportExportTypeValue}
          />
        )}
        {!hideExportButton && (
          <ExportButtonMenu
            tableHeaders={exportHeaders}
            generateCSVData={generateCSVData ? generateCSVData() : undefined}
            formattedData={formattedData}
            //@ts-ignore
            generatePDF={generatePDF}
            disabled={disableExportButton}
            exportButtonLoading={exportButtonLoading}
          />
        )}
        {reportType === 8 && (
          <Button
            id="generate-report-button"
            size="small"
            sx={{ width: "120px" }}
            theme={theme}
            onClick={handleGenerateReport}
            disabled={dateRangeLoading}
          >
            {t("buttonsText.generate")}
          </Button>
        )}

        {exportModalButton && (
          <Fragment>
            <MuiButton
              disabled={disableExportButton}
              id="demo-positioned-button"
              aria-haspopup="true"
              onClick={() => setOpenModal && setOpenModal(true)}
              variant="text"
              sx={{
                textTransform: "none"
              }}
            >
              {disableExportButton ? (
                <ExportReportIcon style={{ width: "14px", height: "14px" }} />
              ) : (
                <ExportReportActiveIcon
                  style={{ width: "14px", height: "14px" }}
                />
              )}
              <span style={{ marginTop: "2px" }}>{t("typography.export")}</span>
            </MuiButton>
            <ExportReportsForm
              open={openModal}
              onClose={() => setOpenModal && setOpenModal(false)}
              onSubmit={exportReportsSubmitHandler}
              theme={theme}
            />
          </Fragment>
        )}
      </Box>
    </Box>
  );
};
export default ReportsFilterBar;
